import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";

import { useAuth } from "./AuthContext.js";
import "./login.css";

/**
 * Real login screen for the hosted deployment — replaces the Phase-4
 * placeholder that only collected a display name. Not reachable at all on a
 * desktop deployment (`RequireAuth` never redirects there, and `AppRoutes`
 * bounces `/login` straight back to `/` on desktop).
 *
 * Errors are always the single generic message `AuthContext.login` returns
 * (via `describeApiError`) — a wrong password and a nonexistent email
 * produce the exact same text, matching the server's "wrong email or
 * password" framing (`apps/server/src/auth/login.ts`). This page does not,
 * and must not, try to be smarter than that response.
 */
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await auth.login(email, password);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      const from = (location.state as { from?: Location } | null)?.from ?? null;
      navigate(from ? `${from.pathname}${from.search}` : "/", { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={(e) => void handleSubmit(e)}>
        <h1>Prijava</h1>
        <label htmlFor="login-email">E-poštni naslov</label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          required
          disabled={submitting}
        />
        <label htmlFor="login-password">Geslo</label>
        <input
          id="login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          disabled={submitting}
        />
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? "Prijavljanje …" : "Vstopi"}
        </button>
      </form>
    </main>
  );
}
